/**
 * AuthorshipCertificate - Certificado Visual de Autoria
 * 
 * Gera um certificado elegante com:
 * - Dados de participação de cada autor
 * - Hash de verificação
 * - QR Code para validação
 * - Opção de download em PDF/imagem
 */

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { QRCodeSVG } from 'qrcode.react';
import { 
  Award, 
  Download, 
  Share2, 
  CheckCircle2, 
  Shield,
  Calendar,
  BookOpen,
  Users,
  Fingerprint,
  ExternalLink,
  Loader2,
  Copy,
  Check
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AuthorshipCertificate({ bookId, bookTitle, onClose }) {
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);
  const certificateRef = useRef(null);

  useEffect(() => {
    if (bookId) {
      fetchCertificate();
    }
  }, [bookId]);

  const fetchCertificate = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/coautoria/certificate/${bookId}`,
        { withCredentials: true }
      );
      setCertificate(response.data);
    } catch (err) {
      console.error('Error fetching certificate:', err);
      toast.error('Não foi possível gerar o certificado');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!certificateRef.current) return;
    
    setDownloading(true);
    try {
      const canvas = await html2canvas(certificateRef.current, {
        scale: 2,
        backgroundColor: '#1a1a1a',
        logging: false
      });
      
      const link = document.createElement('a');
      link.download = `certificado-autoria-${bookId}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.success('Certificado baixado com sucesso!');
    } catch (err) {
      console.error('Error downloading certificate:', err);
      toast.error('Erro ao baixar certificado');
    } finally {
      setDownloading(false);
    }
  };

  const handleCopyHash = () => {
    if (certificate?.verification_hash) {
      navigator.clipboard.writeText(certificate.verification_hash);
      setCopied(true);
      toast.success('Hash copiado!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShare = async () => {
    if (!certificate) return;
    
    const shareData = {
      title: `Certificado de Autoria - ${certificate.book_title}`,
      text: `Certificado de autoria da obra "${certificate.book_title}" com hash de verificação: ${certificate.verification_hash}`,
      url: certificate.verification_url || window.location.href
    };
    
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        navigator.clipboard.writeText(shareData.text);
        toast.success('Link copiado para compartilhar!');
      }
    } catch (err) {
      // Share cancelled by user - silent fail is expected
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString('pt-BR');
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center">
        <div className="bg-gray-900 rounded-2xl p-8 flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 text-amber-400 animate-spin" />
          <p className="text-gray-400">Gerando certificado...</p>
        </div>
      </div>
    );
  }

  if (!certificate) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center">
        <div className="bg-gray-900 rounded-2xl p-8 text-center">
          <p className="text-red-400 mb-4">Não foi possível gerar o certificado</p>
          <Button onClick={onClose} variant="outline">Fechar</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-auto">
      <div className="max-w-3xl w-full space-y-4">
        {/* Actions Bar */}
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Award className="h-6 w-6 text-amber-400" />
            Certificado de Autoria
          </h2>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleShare}
              className="border-white/20 text-white hover:bg-white/10"
            >
              <Share2 className="h-4 w-4 mr-2" />
              Compartilhar
            </Button>
            <Button 
              size="sm" 
              onClick={handleDownload}
              disabled={downloading}
              className="bg-amber-500 hover:bg-amber-600 text-black"
              data-testid="download-certificate-btn"
            >
              {downloading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Baixar PNG
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onClose}
              className="text-gray-400 hover:text-white"
            >
              Fechar
            </Button>
          </div>
        </div>

        {/* Certificate */}
        <div 
          ref={certificateRef}
          className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl overflow-hidden border border-amber-500/30 shadow-2xl"
          data-testid="certificate-container"
        >
          {/* Header Ornament */}
          <div className="h-2 bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500" />
          
          <div className="p-8 space-y-6">
            {/* Title */}
            <div className="text-center space-y-2">
              <div className="flex items-center justify-center gap-3">
                <div className="h-px w-16 bg-gradient-to-r from-transparent to-amber-500/50" />
                <Award className="h-10 w-10 text-amber-400" />
                <div className="h-px w-16 bg-gradient-to-l from-transparent to-amber-500/50" />
              </div>
              <h1 className="text-3xl font-serif font-bold text-white tracking-wide">
                CERTIFICADO DE AUTORIA
              </h1>
              <p className="text-amber-400/80 text-sm uppercase tracking-widest">
                Registro de Propriedade Intelectual
              </p>
            </div>

            {/* Book Info */}
            <div className="bg-white/5 rounded-xl p-6 text-center border border-white/10">
              <div className="flex items-center justify-center gap-2 text-gray-400 mb-2">
                <BookOpen className="h-4 w-4" />
                <span className="text-sm uppercase tracking-wider">Obra Literária</span>
              </div>
              <h2 className="text-2xl font-serif font-bold text-white mb-2">
                {certificate.book_title}
              </h2>
              <div className="flex items-center justify-center gap-4 text-sm text-gray-400">
                <span>{formatNumber(certificate.total_words)} palavras</span>
                <span>•</span>
                <span>{certificate.total_chapters} capítulos</span>
              </div>
            </div>

            {/* Authors */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-gray-400">
                <Users className="h-4 w-4" />
                <span className="text-sm uppercase tracking-wider">Autores Certificados</span>
              </div>
              
              <div className="grid gap-3">
                {certificate.authors?.map((author, index) => (
                  <div 
                    key={author.user_id || index}
                    className="bg-white/5 rounded-lg p-4 flex items-center justify-between border border-white/5"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold">
                        {(author.user_name || 'A').charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium text-white">{author.user_name}</div>
                        <div className="text-sm text-gray-500">
                          {author.net_contribution >= 0 ? '+' : ''}{formatNumber(author.net_contribution)} palavras
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-amber-400">
                        {author.contribution_percentage}%
                      </div>
                      <Badge variant="outline" className="text-xs border-amber-500/30 text-amber-400/80">
                        {!author.invited_by ? 'Criador' : 'Co-autor'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Period */}
            <div className="grid grid-cols-2 gap-4 text-center">
              <div className="bg-white/5 rounded-lg p-3 border border-white/5">
                <div className="flex items-center justify-center gap-2 text-gray-400 mb-1">
                  <Calendar className="h-3 w-3" />
                  <span className="text-xs uppercase">Início</span>
                </div>
                <div className="text-white font-medium">{formatDate(certificate.period_start)}</div>
              </div>
              <div className="bg-white/5 rounded-lg p-3 border border-white/5">
                <div className="flex items-center justify-center gap-2 text-gray-400 mb-1">
                  <Calendar className="h-3 w-3" />
                  <span className="text-xs uppercase">Emissão</span>
                </div>
                <div className="text-white font-medium">{formatDate(certificate.generated_at)}</div>
              </div>
            </div>

            {/* Verification with QR Code */}
            <div className="bg-gradient-to-r from-green-500/10 via-green-500/5 to-green-500/10 rounded-xl p-4 border border-green-500/20">
              <div className="flex items-center gap-4">
                {/* QR Code */}
                <div className="flex-shrink-0 p-2 bg-white rounded-lg">
                  <QRCodeSVG
                    value={certificate.verification_url || `${window.location.origin}/verificar/${certificate.verification_hash}`}
                    size={80}
                    level="H"
                    includeMargin={false}
                    bgColor="#ffffff"
                    fgColor="#1a1a1a"
                  />
                </div>
                
                {/* Verification Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="h-5 w-5 text-green-400" />
                    <CheckCircle2 className="h-4 w-4 text-green-400" />
                    <span className="text-green-400 font-medium">Certificado Verificável</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Fingerprint className="h-3 w-3 text-gray-500" />
                    <code className="text-xs text-gray-400 font-mono">
                      {certificate.verification_hash}
                    </code>
                    <button 
                      onClick={handleCopyHash}
                      className="p-1 hover:bg-white/10 rounded transition-colors"
                    >
                      {copied ? (
                        <Check className="h-3 w-3 text-green-400" />
                      ) : (
                        <Copy className="h-3 w-3 text-gray-500" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Escaneie o QR Code para verificar a autenticidade
                  </p>
                </div>
                
                {/* External Link */}
                {certificate.verification_url && (
                  <a 
                    href={certificate.verification_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors flex-shrink-0"
                  >
                    <ExternalLink className="h-4 w-4 text-gray-400" />
                  </a>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="text-center pt-4 border-t border-white/10">
              <p className="text-xs text-gray-500">
                Este certificado foi gerado automaticamente pelo sistema AuthorsKey.
                <br />
                O hash de verificação garante a autenticidade e integridade dos dados.
              </p>
            </div>
          </div>

          {/* Footer Ornament */}
          <div className="h-2 bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500" />
        </div>
      </div>
    </div>
  );
}
